import {
    ClientManagerInstanceToken,
    CompositeModel,
    ConfigService,
    CustomInjector,
    DisplayDecorator,
    DropdownModel,
    NOTIFICATION_STATUS_CODE,
    NotificationModel,
    NotificationService,
    NotificationUpdateService,
    STATUS_STEP_FOR_NON_REMOTE_WORK,
    STATUS_STEP_FOR_REMOTE_WORK,
    TextModel,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    ValueHelperService,
} from '@evorait/pace-core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { STATUS_STEP_ACTION } from './status-step-action.enum';
import moment from 'moment';

export class StatusStepButtonModel extends CompositeModel {
    public actions: DropdownModel<TextModel>;
    public isEscalated = false;
    public readonly ESCALATE = 'ESCALATE';
    public constructor(notification: NotificationModel) {
        super('StatusStepButtonModel');

        const injector = CustomInjector.getInjector();
        const log = injector.get(LogInstanceToken);
        const translate = injector.get(TranslateService);
        const notificationService = injector.get(NotificationService);
        const notificationUpdateService = injector.get(NotificationUpdateService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        const configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);

        const actionEntries: Array<DisplayDecorator<TextModel>> = [];

        // first add the actions for the normal notification status changes
        const status = notificationService.getStatus(notification);
        const availableStatus = notificationService.getAvailableStatus(notification);

        log.debug(
            `StatusStepButtonModel: Notification ${notification.identifier.toString()} has local status '${
                status.statusCode
            }'; available status: ${availableStatus.join(' ')}`,
        );

        if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.ETA) !== -1) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_ETA_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.ETA),
                ),
            );
        }

        if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.TRAV) !== -1) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_TRAV_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.TRAV),
                ),
            );
        }

        if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.OSR) !== -1) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_OSR_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.OSR),
                ),
            );
        }
        if (configService.getEnableSafetyAssessmentForm()) {
            if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.ATS) !== -1) {
                actionEntries.push(
                    new DisplayDecorator<TextModel>(
                        <string>translate.instant('NOTIFICATION_STATUS_SAF_LABEL'),
                        new TextModel('nextStatus', STATUS_STEP_ACTION.ATS),
                    ),
                );
            }
        }

        // SOSR: Special OSR for PACE US
        if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.SOSR) !== -1) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_SOSR_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.SOSR),
                ),
            );
        }

        // add confirmation
        if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.CRM) !== -1) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_CRM_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.CRM),
                ),
            );
        }

        // add short confirmation
        if (clientManager.isNgmP58OrSA() && availableStatus.indexOf(NOTIFICATION_STATUS_CODE.SCRM) !== -1) {
            // check if notification was cancelled
            const today = moment();
            if (
                !status.isCancelled() &&
                !notificationService.isNotificationCancelled(notification) &&
                !notificationUpdateService.checkIfCancellationExists(notification.identifier) &&
                today.startOf('day').isBefore(notificationService.getNotificationEnd(notification).startOf('day'))
            ) {
                const shortCRM = new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_SCRM_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.SCRM),
                );
                // For P58, if short confirmation available then it should be on top
                if (clientManager.isNgmP58()) {
                    actionEntries.unshift(shortCRM);
                } else {
                    actionEntries.push(shortCRM);
                }
            }
        }

        // add escalation
        this.isEscalated = notificationService.getEscalated(notification);
        if (status.canEscalate()) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_HSCE_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.ESCALATE),
                ),
            );
        }

        // add PACE Forms if the notifications is in status ETA, TRAV or OSR or TOBS
        if (
            notification.isPaceFormsEnabled &&
            (status.statusCode === NOTIFICATION_STATUS_CODE.ETA ||
                status.statusCode === NOTIFICATION_STATUS_CODE.TRAV ||
                status.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
                status.statusCode === NOTIFICATION_STATUS_CODE.SOSR ||
                status.statusCode === NOTIFICATION_STATUS_CODE.ATS ||
                status.isToBeSigned() ||
                status.isRemoteWork())
        ) {
            actionEntries.push(
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('NOTIFICATION_STATUS_F4M_LABEL'),
                    new TextModel('nextStatus', STATUS_STEP_ACTION.FORMS),
                ),
            );
        }
        // As per requirement, if Notification user status is REMT,
        // then  remote support and Remote work/Training should be displayed on the top of the Step entries
        // or else to the bottom
        if (configService.getEnableRemoteWork()) {
            if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.REMT) !== -1) {
                if (notification.isRemoteSupport) {
                    actionEntries.push(
                        new DisplayDecorator<TextModel>(
                            <string>translate.instant('NOTIFICATION_STATUS_REMT_LABEL'),
                            new TextModel('nextStatus', STATUS_STEP_ACTION.REMT),
                        ),
                    );
                }
            }
            const timeRecordingTask = valueHelperService.getTimeRecordingTaskTypeInfo();
            let remoteWorkLabel;
            const rosrTask = timeRecordingTask.find((value) => value.code === NOTIFICATION_STATUS_CODE.ROSR);
            const rosnTask = timeRecordingTask.find((value) => value.code === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN);
            if (rosnTask) {
                remoteWorkLabel = <string>translate.instant('NOTIFICATION_STATUS_ROSR_LABEL');
            } else {
                remoteWorkLabel = rosrTask?.descText;
            }
            // Remote clarification FCC is allowed only for few Notification type maintained in gateway
            let remoteClarificationLabel;
            if (configService.getShowFCCForNotificationTypes(notification.notificationType)) {
                const fccTask = timeRecordingTask.find((value) => value.code === NOTIFICATION_STATUS_CODE.FCC);
                const fccnTask = timeRecordingTask.find(
                    (value) => value.code === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN,
                );
                if (fccnTask) {
                    remoteClarificationLabel = <string>translate.instant('NOTIFICATION_STATUS_FCC_LABEL');
                } else {
                    remoteClarificationLabel = fccTask?.descText;
                }
            }

            if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.ROSR) !== -1 && remoteWorkLabel) {
                actionEntries.push(
                    new DisplayDecorator<TextModel>(
                        remoteWorkLabel,
                        new TextModel('nextStatus', STATUS_STEP_ACTION.ROSR),
                    ),
                );
            }
            if (availableStatus.indexOf(NOTIFICATION_STATUS_CODE.FCC) !== -1 && remoteClarificationLabel) {
                actionEntries.push(
                    new DisplayDecorator<TextModel>(
                        remoteClarificationLabel,
                        new TextModel('nextStatus', STATUS_STEP_ACTION.FCC),
                    ),
                );
            }

            const statusOrder =
                notification.updatedStatus.isOnSiteWork() || !notification.isRemoteSupport
                    ? STATUS_STEP_FOR_NON_REMOTE_WORK
                    : STATUS_STEP_FOR_REMOTE_WORK;

            const remoteWorkStatusOrder = Object.values(statusOrder);
            actionEntries.sort(
                (a, b) =>
                    remoteWorkStatusOrder.indexOf(
                        <STATUS_STEP_FOR_REMOTE_WORK | STATUS_STEP_FOR_NON_REMOTE_WORK>a.model.value,
                    ) -
                    remoteWorkStatusOrder.indexOf(
                        <STATUS_STEP_FOR_REMOTE_WORK | STATUS_STEP_FOR_NON_REMOTE_WORK>b.model.value,
                    ),
            );
        }
        log.debug(
            `StatusStepButtonModel: Created step button entries: ${actionEntries.map((e) => e.displayName).join(', ')}`,
        );

        // finally create the dropdown model for the component
        this.actions = <DropdownModel<TextModel>>this.set(new DropdownModel('AvailableStatus', actionEntries));
        this.actions.selectByIndex(0); // preselect first entry
    }
}
